import React, { useState, useMemo } from 'react';
import { fmt, fmtDate, MES_LABEL, esVenta, esCompra, SUB_LABELS } from './constants';

export default function LibroContable({ movimientos, mode }) {
  const isVentas = mode==='ventas';
  const [filtroMes,  setFiltroMes]  = useState('');
  const [filtroSub,  setFiltroSub]  = useState('todos');
  const [buscar,     setBuscar]     = useState('');
  const [page,       setPage]       = useState(1);
  const PER = 60;

  const registros = useMemo(()=>
    movimientos.filter(isVentas ? esVenta : esCompra)
      .sort((a,b)=>b.fecha.localeCompare(a.fecha))
  ,[movimientos, isVentas]);

  const meses = useMemo(()=>
    [...new Set(registros.map(m=>m.fecha.slice(0,7)))].sort().reverse()
  ,[registros]);

  const subtipos = useMemo(()=>
    [...new Set(registros.map(m=>m.subTipo).filter(Boolean))]
  ,[registros]);

  const filtered = useMemo(()=>registros.filter(m=>{
    if (filtroMes && !m.fecha.startsWith(filtroMes)) return false;
    if (filtroSub!=='todos' && m.subTipo!==filtroSub) return false;
    if (buscar){
      const q=buscar.toLowerCase();
      if (![m.descripcion,m.razonSocial,m.nFactura,m.pn,m.rutContraparte].join(' ').toLowerCase().includes(q)) return false;
    }
    return true;
  }),[registros,filtroMes,filtroSub,buscar]);

  const paged = useMemo(()=>filtered.slice((page-1)*PER,page*PER),[filtered,page]);
  const totalPages = Math.ceil(filtered.length/PER);

  /* totales por mes para resumen */
  const porMes = useMemo(()=>{
    const map={};
    filtered.forEach(m=>{
      const mes=m.fecha.slice(0,7);
      if (!map[mes]) map[mes]={neto:0,iva:0,total:0,n:0};
      map[mes].neto  += m.monto;
      map[mes].iva   += m.monto_iva||0;
      map[mes].total += m.monto_total||m.monto;
      map[mes].n     += 1;
    });
    return Object.entries(map).sort((a,b)=>b[0].localeCompare(a[0]));
  },[filtered]);

  const totNeto  = filtered.reduce((a,m)=>a+m.monto,0);
  const totIva   = filtered.reduce((a,m)=>a+(m.monto_iva||0),0);
  const totTotal = filtered.reduce((a,m)=>a+(m.monto_total||m.monto),0);

  const color    = isVentas ? '#10b981' : '#ef4444';
  const icon     = isVentas ? 'ti-file-invoice' : 'ti-shopping-cart';
  const titulo   = isVentas ? 'Libro de Ventas' : 'Libro de Compras';

  /* exportar este libro */
  const handleExport = ()=>{
    const h=['Fecha','Tipo','N° Factura','RUT','Razón Social','P/N','Descripción','Monto Neto','IVA','Total','Notas'];
    const rows=filtered.map(m=>[
      m.fecha, SUB_LABELS[m.subTipo]||m.subTipo||'',
      m.nFactura||'',m.rutContraparte||'',m.razonSocial||'',
      m.pn||'',m.descripcion||'',m.monto,m.monto_iva||0,m.monto_total||m.monto,m.notas||''
    ]);
    const csv=[h,...rows].map(r=>r.map(c=>`"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob=new Blob(['﻿'+csv],{type:'text/csv;charset=utf-8;'});
    const url=URL.createObjectURL(blob);
    const a=document.createElement('a');
    a.href=url; a.download=`${mode}_${filtroMes||'total'}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div>
      {/* Cabecera */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16,flexWrap:'wrap',gap:8}}>
        <h2 style={{fontSize:15,fontWeight:700}}>
          <i className={`ti ${icon}`} style={{marginRight:8,color}} />{titulo}
        </h2>
        <button onClick={handleExport} style={{fontSize:12}}>
          <i className="ti ti-download" />Exportar CSV
        </button>
      </div>

      {/* Resumen por mes */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:8,marginBottom:16}}>
        {porMes.slice(0,6).map(([mes,d])=>(
          <div key={mes} onClick={()=>{setFiltroMes(filtroMes===mes?'':mes);setPage(1);}} style={{
            background:'var(--card)',border:`1px solid ${filtroMes===mes?color:'var(--border)'}`,
            borderTop:`3px solid ${color}`,borderRadius:'var(--radius)',
            padding:'10px 12px',cursor:'pointer',transition:'border-color 0.15s',
          }}>
            <p style={{fontSize:12,color:'var(--text2)',marginBottom:4}}>{MES_LABEL(mes)}</p>
            <p style={{fontWeight:700,fontSize:14,color}}>{fmt(d.neto)}</p>
            <p style={{fontSize:10,color:'var(--text3)'}}>{d.n} reg · IVA {fmt(d.iva)}</p>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:12,alignItems:'center'}}>
        <select value={filtroMes} onChange={e=>{setFiltroMes(e.target.value);setPage(1);}} style={{width:'auto',minWidth:120}}>
          <option value="">Todos los meses</option>
          {meses.map(m=><option key={m} value={m}>{m}</option>)}
        </select>
        {subtipos.length>1 && (
          <select value={filtroSub} onChange={e=>{setFiltroSub(e.target.value);setPage(1);}} style={{width:'auto',minWidth:140}}>
            <option value="todos">Todos los subtipos</option>
            {subtipos.map(s=><option key={s} value={s}>{SUB_LABELS[s]||s}</option>)}
          </select>
        )}
        <input type="text" placeholder="Buscar RUT, descripción, P/N..." value={buscar}
          onChange={e=>{setBuscar(e.target.value);setPage(1);}} style={{flex:1,minWidth:160}} />
        <span style={{fontSize:12,color:'var(--text3)',whiteSpace:'nowrap'}}>{filtered.length} registros</span>
      </div>

      {/* Tabla */}
      <div style={{overflowX:'auto',borderRadius:'var(--radius)',border:'1px solid var(--border)'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead>
            <tr style={{background:'var(--bg2)'}}>
              {['Fecha','Tipo','N° Factura','Razón Social / Proveedor','P/N','Descripción','Neto','IVA','Total'].map(h=>(
                <th key={h} style={{padding:'9px 10px',textAlign:'left',fontWeight:600,fontSize:11,color:'var(--text2)',borderBottom:'1px solid var(--border)',whiteSpace:'nowrap'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paged.length===0
              ? <tr><td colSpan={9} style={{textAlign:'center',padding:'2.5rem',color:'var(--text3)'}}>Sin registros para este filtro</td></tr>
              : paged.map((m,i)=>{
                  const isSF=m.subTipo==='sin_factura';
                  return (
                    <tr key={m.id} style={{background:i%2===0?'var(--card)':'transparent',borderBottom:'1px solid var(--border)',opacity:isSF?0.8:1}}>
                      <td style={{padding:'8px 10px',whiteSpace:'nowrap',color:'var(--text2)'}}>{m.fecha}</td>
                      <td style={{padding:'8px 10px',whiteSpace:'nowrap'}}>
                        <span style={{fontSize:10,padding:'2px 7px',borderRadius:99,
                          background: isSF?'rgba(245,158,11,0.12)':m.tipoDoc==='IMP'?'rgba(59,130,246,0.12)':'rgba(16,185,129,0.12)',
                          color: isSF?'var(--amber)':m.tipoDoc==='IMP'?'var(--blue)':'var(--green)',
                          border:`1px solid ${isSF?'rgba(245,158,11,0.3)':m.tipoDoc==='IMP'?'rgba(59,130,246,0.3)':'rgba(16,185,129,0.3)'}`,
                        }}>{SUB_LABELS[m.subTipo]||m.subTipo||m.tipoDoc}</span>
                      </td>
                      <td style={{padding:'8px 10px',color:'var(--text2)',whiteSpace:'nowrap'}}>{m.nFactura||'-'}</td>
                      <td style={{padding:'8px 10px',maxWidth:180,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={m.razonSocial}>{m.razonSocial||'-'}</td>
                      <td style={{padding:'8px 10px',color:'var(--text3)',fontFamily:'monospace',fontSize:11,whiteSpace:'nowrap'}}>{m.pn||'-'}</td>
                      <td style={{padding:'8px 10px',maxWidth:220,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={m.descripcion}>{m.descripcion||'-'}</td>
                      <td style={{padding:'8px 10px',textAlign:'right',fontWeight:600,color,whiteSpace:'nowrap'}}>{fmt(m.monto)}</td>
                      <td style={{padding:'8px 10px',textAlign:'right',color:'var(--text2)',whiteSpace:'nowrap'}}>{m.monto_iva?fmt(m.monto_iva):'-'}</td>
                      <td style={{padding:'8px 10px',textAlign:'right',fontWeight:500,whiteSpace:'nowrap'}}>{fmt(m.monto_total||m.monto)}</td>
                    </tr>
                  );
                })
            }
          </tbody>
          {paged.length>0 && (
            <tfoot>
              <tr style={{background:'var(--bg2)',borderTop:'2px solid var(--border)'}}>
                <td colSpan={6} style={{padding:'8px 10px',fontWeight:700,fontSize:12}}>TOTAL ({filtered.length} registros)</td>
                <td style={{padding:'8px 10px',textAlign:'right',fontWeight:700,color}}>{fmt(totNeto)}</td>
                <td style={{padding:'8px 10px',textAlign:'right',fontWeight:700,color:'var(--blue)'}}>{fmt(totIva)}</td>
                <td style={{padding:'8px 10px',textAlign:'right',fontWeight:700}}>{fmt(totTotal)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Paginación */}
      {totalPages>1 && (
        <div style={{display:'flex',gap:6,justifyContent:'center',marginTop:12,alignItems:'center'}}>
          <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} style={{padding:'5px 12px',fontSize:12}}>‹ Anterior</button>
          <span style={{fontSize:12,color:'var(--text2)'}}>{page} / {totalPages}</span>
          <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages} style={{padding:'5px 12px',fontSize:12}}>Siguiente ›</button>
        </div>
      )}
    </div>
  );
}
